/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { collection, getDocs, onSnapshot, doc, query, orderBy, where } from "firebase/firestore";
import { db_modular as db } from "../../firebaseConfig";
import { UserAuth } from "../../context/authContext";
import toast from "react-hot-toast";
import { FiTrendingUp, FiBox, FiSend, FiCheck, FiDownload, FiAward } from "react-icons/fi";
import { clsx } from "clsx";

const HackLeaderboard = () => {
  const [id] = useState(() => localStorage.getItem("roomId") || "");
  const [redTeamStats, setRedTeamStats] = useState([]);
  const [blueTeamStats, setBlueTeamStats] = useState([]);
  const [redTeamBlocks, setRedTeamBlocks] = useState(0);
  const [blueTeamBlocks, setBlueTeamBlocks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState("blocksMined");
  const [leaderboardMode, setLeaderboardMode] = useState("room");
  const [globalStats, setGlobalStats] = useState([]);
  const { userData } = UserAuth();
  const userCardRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Download leaderboard card as image
  const downloadLeaderboardCard = async (playerData, teamName) => {
    if (!playerData) {
      toast.error("Player data not found");
      return;
    }

    try {
      toast.loading("Generating image...");

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = 1000;
      canvas.height = 400;

      // Draw background gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, teamName === "Red Team" ? "#4a1010" : "#101a4a");
      gradient.addColorStop(1, "#0f0f1e");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw border
      const borderColor =
        teamName === "Red Team" ? "rgba(239, 68, 68, 0.3)" : "rgba(59, 130, 246, 0.3)";
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 2;
      ctx.roundRect(20, 20, canvas.width - 40, canvas.height - 40, 15);
      ctx.stroke();

      // Draw rank badge
      const rankX = 60;
      const rankY = 80;
      const rankSize = 80;

      let rankColor = "#1B1E3E";
      if (playerData.rank === 1) rankColor = "#fbbf24";
      else if (playerData.rank === 2) rankColor = "#d1d5db";
      else if (playerData.rank === 3) rankColor = "#fb923c";

      ctx.fillStyle = rankColor;
      ctx.beginPath();
      ctx.arc(rankX + rankSize / 2, rankY + rankSize / 2, rankSize / 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = playerData.rank === 2 ? "#1f2937" : "white";
      ctx.font = "bold 50px Arial";
      ctx.textAlign = "center";
      ctx.fillText(playerData.rank, rankX + rankSize / 2, rankY + rankSize / 2 + 20);

      // Draw user info
      ctx.fillStyle = "white";
      ctx.textAlign = "left";
      ctx.font = "bold 36px Arial";
      ctx.fillText(playerData.email?.split("@")[0] || "User", 200, 100);

      ctx.fillStyle = teamName === "Red Team" ? "#ef4444" : "#3b82f6";
      ctx.font = "bold 18px Arial";
      ctx.fillText(teamName, 200, 135);

      // Draw stats
      const statsY = 220;
      const statWidth = (canvas.width - 100) / 3;
      const statX = [80, 80 + statWidth, 80 + statWidth * 2];

      const stats = [
        { label: "Blocks Mined", value: playerData.blocksMined || 0 },
        { label: "TX Created", value: playerData.txCreated || 0 },
        { label: "TX Verified", value: playerData.txVerified || 0 },
      ];

      stats.forEach((stat, index) => {
        ctx.fillStyle = "rgba(30, 30, 62, 0.8)";
        ctx.fillRect(statX[index], statsY, statWidth - 20, 120);

        ctx.fillStyle = "#a0a0b0";
        ctx.font = "14px Arial";
        ctx.fillText(stat.label, statX[index] + 15, statsY + 35);

        ctx.fillStyle = teamName === "Red Team" ? "#ef4444" : "#3b82f6";
        ctx.font = "bold 32px Arial";
        ctx.fillText(stat.value, statX[index] + 15, statsY + 85);
      });

      // Draw footer
      ctx.fillStyle = "#606070";
      ctx.font = "12px Arial";
      ctx.textAlign = "center";
      ctx.fillText(
        `AstraHack Leaderboard • ${teamName} • Position #${playerData.rank}`,
        canvas.width / 2,
        canvas.height - 20
      );

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `leaderboard-${teamName.toLowerCase().replace(" ", "-")}-${playerData.email?.split("@")[0] || "user"}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.dismiss();
        toast.success("Card downloaded successfully!");
      }, "image/png");
    } catch (error) {
      console.error("Error downloading card:", error);
      toast.dismiss();
      toast.error("Failed to download card. Please try again.");
    }
  };

  const fetchStats = useCallback(async () => {
    if (!id && leaderboardMode === "room") {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      if (leaderboardMode === "global") {
        const roomsSnap = await getDocs(collection(db, "rooms"));
        const allStats = {};

        const processRoomData = async (roomDoc) => {
          const roomId = roomDoc.id;
          const [redTx, blueTx, redBlocks, blueBlocks] = await Promise.all([
            getDocs(collection(db, "rooms", roomId, "redTeamTransactions")),
            getDocs(collection(db, "rooms", roomId, "blueTeamTransactions")),
            getDocs(collection(db, "rooms", roomId, "redTeamBlocks")),
            getDocs(collection(db, "rooms", roomId, "blueTeamBlocks")),
          ]);

          const results = [];

          const extractStats = (txSnap, blocksSnap) => {
            txSnap.forEach((doc) => {
              const tx = doc.data();
              if (tx.senderEmail === "system" || tx.sender === "AstraMiner/AstraPOS/AstraHack")
                return;
              results.push({ type: "tx", data: tx });
            });

            blocksSnap.forEach((doc) => {
              const block = doc.data();
              results.push({ type: "block", data: block });
            });
          };

          extractStats(redTx, redBlocks);
          extractStats(blueTx, blueBlocks);
          return results;
        };

        const allRoomsResults = await Promise.all(roomsSnap.docs.map(processRoomData));

        allRoomsResults.flat().forEach((result) => {
          if (result.type === "tx") {
            const tx = result.data;
            if (!allStats[tx.sender])
              allStats[tx.sender] = {
                publicKey: tx.sender,
                email: tx.senderEmail || "Unknown",
                blocksMined: 0,
                txCreated: 0,
                txVerified: 0,
              };
            allStats[tx.sender].txCreated++;
            if (tx.minerPublicKey && tx.verified) {
              if (!allStats[tx.minerPublicKey])
                allStats[tx.minerPublicKey] = {
                  publicKey: tx.minerPublicKey,
                  email: tx.minerEmail || "Unknown",
                  blocksMined: 0,
                  txCreated: 0,
                  txVerified: 0,
                };
              allStats[tx.minerPublicKey].txVerified++;
            }
          } else if (result.type === "block") {
            const block = result.data;
            const miners = new Set();
            block.transactions?.forEach((tx) => {
              if (tx.minerPublicKey) miners.add(tx.minerPublicKey);
            });
            miners.forEach((pk) => {
              if (!allStats[pk])
                allStats[pk] = {
                  publicKey: pk,
                  email: "Unknown",
                  blocksMined: 0,
                  txCreated: 0,
                  txVerified: 0,
                };
              allStats[pk].blocksMined++;
            });
          }
        });

        setGlobalStats(Object.values(allStats));
        setLoading(false);
        return;
      }

      // Room Mode
      const roomDoc = await getDocs(query(collection(db, "rooms"), where("__name__", "==", id)));
      let allRoomMembers = {};

      if (!roomDoc.empty) {
        const roomData = roomDoc.docs[0].data();
        const memberPks = [
          ...(roomData.red_participants || []),
          ...(roomData.blue_participants || []),
          ...(roomData.red_miners || []),
          ...(roomData.blue_miners || []),
        ];

        if (memberPks.length > 0) {
          const usersSnap = await getDocs(
            query(collection(db, "users"), where("publicKey", "in", memberPks))
          );
          usersSnap.forEach((doc) => {
            const data = doc.data();
            allRoomMembers[data.publicKey] = {
              publicKey: data.publicKey,
              email: data.email,
              blocksMined: 0,
              txCreated: 0,
              txVerified: 0,
              balance: data.balance || 0,
              team:
                roomData.red_participants?.includes(data.publicKey) ||
                roomData.red_miners?.includes(data.publicKey)
                  ? "red"
                  : "blue",
            };
          });
        }
      }

      const [redBlocksSnap, blueBlocksSnap] = await Promise.all([
        getDocs(collection(db, "rooms", id, "redTeamBlocks")),
        getDocs(collection(db, "rooms", id, "blueTeamBlocks")),
      ]);
      setRedTeamBlocks(redBlocksSnap.size);
      setBlueTeamBlocks(blueBlocksSnap.size);

      const [redTxSnap, blueTxSnap] = await Promise.all([
        getDocs(collection(db, "rooms", id, "redTeamTransactions")),
        getDocs(collection(db, "rooms", id, "blueTeamTransactions")),
      ]);

      const processStats = (txSnapshot, blocksSnapshot, team) => {
        const stats = {};

        // Initialize with room members for this team
        Object.values(allRoomMembers)
          .filter((m) => m.team === team)
          .forEach((m) => {
            stats[m.publicKey] = { ...m };
          });

        blocksSnapshot.forEach((blockDoc) => {
          const block = blockDoc.data();
          if (block.transactions && block.transactions.length > 0) {
            const minersInBlock = new Map();
            block.transactions.forEach((tx) => {
              if (tx.minerPublicKey) {
                minersInBlock.set(tx.minerPublicKey, tx.minerEmail || "Unknown");
              }
            });
            minersInBlock.forEach((email, minerPk) => {
              if (!stats[minerPk]) {
                stats[minerPk] = {
                  publicKey: minerPk,
                  email,
                  blocksMined: 0,
                  txCreated: 0,
                  txVerified: 0,
                  balance: 0,
                };
              }
              stats[minerPk].blocksMined++;
            });
          }
        });

        txSnapshot.forEach((txDoc) => {
          const tx = txDoc.data();
          if (tx.senderEmail === "system" || tx.sender === "AstraMiner/AstraPOS/AstraHack") return;

          if (tx.sender) {
            if (!stats[tx.sender]) {
              stats[tx.sender] = {
                publicKey: tx.sender,
                email: tx.senderEmail || "Unknown",
                blocksMined: 0,
                txCreated: 0,
                txVerified: 0,
                balance: 0,
              };
            }
            stats[tx.sender].txCreated++;
          }

          if (tx.minerPublicKey && tx.verified) {
            if (!stats[tx.minerPublicKey]) {
              stats[tx.minerPublicKey] = {
                publicKey: tx.minerPublicKey,
                email: tx.minerEmail || "Unknown",
                blocksMined: 0,
                txCreated: 0,
                txVerified: 0,
                balance: 0,
              };
            }
            stats[tx.minerPublicKey].txVerified++;
          }
        });

        return Object.values(stats);
      };

      setRedTeamStats(processStats(redTxSnap, redBlocksSnap, "red"));
      setBlueTeamStats(processStats(blueTxSnap, blueBlocksSnap, "blue"));
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load leaderboard");
      console.error(error);
      setLoading(false);
    }
  }, [id, leaderboardMode]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (userCardRef.current && scrollContainerRef.current && !loading) {
      setTimeout(() => {
        const container = scrollContainerRef.current;
        const card = userCardRef.current;
        if (card && container) {
          const cardTop = card.offsetTop;
          const containerHeight = container.clientHeight;
          const cardHeight = card.clientHeight;
          const scrollTop = cardTop - containerHeight / 2 + cardHeight / 2;
          container.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });
        }
      }, 100);
    }
  }, [redTeamStats, blueTeamStats, loading]);

  const sortPlayers = (players) => {
    const sorted = [...players].sort((a, b) => (b[selectedFilter] || 0) - (a[selectedFilter] || 0));
    return sorted.map((p, idx) => ({ ...p, rank: idx + 1 }));
  };

  const getFilterLabel = () => {
    switch (selectedFilter) {
      case "blocksMined":
        return "Blocks Mined";
      case "txCreated":
        return "TX Created";
      case "txVerified":
        return "TX Verified";
      default:
        return "Blocks Mined";
    }
  };

  const getFilterValue = (item) => item[selectedFilter] || 0;

  const TeamSection = ({ teamName, stats, blocks, colorClass, bgClass }) => {
    const sortedStats = sortPlayers(stats);
    const isWinning =
      teamName === "Red Team" ? redTeamBlocks >= blueTeamBlocks : blueTeamBlocks > redTeamBlocks;

    return (
      <div
        className={clsx(
          "rounded-xl border p-6",
          bgClass,
          teamName === "Red Team" ? "border-red-500/20" : "border-blue-500/20"
        )}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className={clsx("text-xl font-bold", colorClass)}>{teamName}</h2>
            {isWinning && <FiAward className="text-yellow-400 text-xl" />}
          </div>
          <div className="flex items-center gap-2 text-text-muted text-sm">
            <FiBox /> Chain: <span className="text-white font-mono">{blocks}</span>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 text-white/50 text-sm font-medium border-b border-white/10 mb-2">
          <div className="flex items-center gap-4 flex-1">
            <span className="w-12">Rank</span>
            <span className="flex-1">User</span>
          </div>
          <span className="w-32 text-right">{getFilterLabel()}</span>
        </div>

        {/* List */}
        <div
          ref={scrollContainerRef}
          className="space-y-2 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar"
        >
          {sortedStats.length > 0 ? (
            sortedStats.map((item) => {
              const isCurrentUser = userData?.publicKey === item.publicKey;
              return (
                <div
                  key={item.publicKey}
                  ref={isCurrentUser ? userCardRef : null}
                  className={clsx(
                    "flex items-center justify-between rounded-xl p-4 transition-all group",
                    isCurrentUser
                      ? "bg-gradient-to-r from-purple-500/30 to-blue-500/30 border border-purple-500/50"
                      : "bg-[#1B1E3E]/60 hover:bg-[#1B1E3E]/80"
                  )}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div
                      className={clsx(
                        "w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg",
                        item.rank === 1
                          ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white"
                          : item.rank === 2
                            ? "bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800"
                            : item.rank === 3
                              ? "bg-gradient-to-br from-orange-400 to-orange-600 text-white"
                              : "bg-[#0F1229]/60 text-white/70"
                      )}
                    >
                      {item.rank}
                    </div>
                    <img
                      src={`https://api.dicebear.com/6.x/avataaars/svg?seed=${item.publicKey}`}
                      alt="avatar"
                      className="w-10 h-10 rounded-full ring-2 ring-white/10"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-semibold text-sm">
                          {item.email?.split("@")[0] || "Unknown"}
                        </h4>
                        {isCurrentUser && (
                          <span className="text-xs px-2 py-0.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full font-semibold">
                            YOU
                          </span>
                        )}
                      </div>
                      <p className="text-white/50 text-xs truncate font-mono">
                        {item.publicKey?.substring(0, 20)}...
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg">
                      <span className="text-white font-bold text-lg">{getFilterValue(item)}</span>
                    </div>
                    {isCurrentUser && (
                      <button
                        onClick={() => downloadLeaderboardCard(item, teamName)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-all"
                        title="Download your card"
                      >
                        <FiDownload className="text-purple-400 hover:text-purple-300" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-text-muted">No activity recorded yet.</div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-text-muted animate-pulse">Loading Leaderboard...</p>
      </div>
    );
  }

  const redIsDominant = redTeamBlocks >= blueTeamBlocks;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Leaderboard</h1>
          <p className="text-white/60 text-sm mt-1">See where you stand</p>
        </div>
      </div>

      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
        {/* Leaderboard Type Toggle */}
        <div className="mb-6">
          <label className="text-white/80 text-sm font-medium mb-3 block">Leaderboard Type</label>
          <select
            value={leaderboardMode}
            onChange={(e) => setLeaderboardMode(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-[#1B1E3E]/60 text-white border border-white/10 focus:border-purple-500 focus:outline-none transition-all"
          >
            <option value="room">Room</option>
            <option value="global">Global</option>
          </select>
        </div>

        {/* Sort By Filter */}
        <div className="mb-6">
          <label className="text-white/80 text-sm font-medium mb-3 block">Sort By</label>
          <div className="flex gap-3">
            {[
              { value: "blocksMined", label: "Blocks Mined", icon: <FiBox /> },
              { value: "txCreated", label: "TX Created", icon: <FiSend /> },
              { value: "txVerified", label: "TX Verified", icon: <FiCheck /> },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                className={clsx(
                  "flex-1 px-4 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2",
                  selectedFilter === filter.value
                    ? "bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white"
                    : "bg-[#1B1E3E]/60 text-white/60 hover:bg-[#1B1E3E]/80"
                )}
              >
                {filter.icon}
                <span className="text-sm">{filter.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Team Leaderboards */}
        {leaderboardMode === "global" ? (
          <TeamSection
            teamName="Global Ranking"
            stats={globalStats}
            blocks={0}
            colorClass="text-purple-400"
            bgClass="bg-purple-500/5"
          />
        ) : !id ? (
          <div className="text-center py-20 px-4">
            <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center text-white/20 mx-auto mb-6">
              <FiBox size={40} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">No Room Joined</h3>
            <p className="text-white/40 max-w-xs mx-auto mb-8">
              Join or create a room to see the team rankings and individual statistics.
            </p>
            <button
              onClick={() => (window.location.href = "/join-room")}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-purple-500/20 transition-all"
            >
              Join a Room
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {redIsDominant ? (
              <>
                <TeamSection
                  teamName="Red Team"
                  stats={redTeamStats}
                  blocks={redTeamBlocks}
                  colorClass="text-red-500"
                  bgClass="bg-red-500/5"
                />
                <TeamSection
                  teamName="Blue Team"
                  stats={blueTeamStats}
                  blocks={blueTeamBlocks}
                  colorClass="text-blue-500"
                  bgClass="bg-blue-500/5"
                />
              </>
            ) : (
              <>
                <TeamSection
                  teamName="Blue Team"
                  stats={blueTeamStats}
                  blocks={blueTeamBlocks}
                  colorClass="text-blue-500"
                  bgClass="bg-blue-500/5"
                />
                <TeamSection
                  teamName="Red Team"
                  stats={redTeamStats}
                  blocks={redTeamBlocks}
                  colorClass="text-red-500"
                  bgClass="bg-red-500/5"
                />
              </>
            )}
          </div>
        )}
      </div>

      <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(27, 30, 62, 0.3);
                    border-radius: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(124, 58, 237, 0.6);
                    border-radius: 5px;
                    border: 2px solid rgba(27, 30, 62, 0.3);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(124, 58, 237, 0.9);
                }
            `}</style>
    </div>
  );
};

export default HackLeaderboard;
